import {
  AnimationAction,
  AnimationClip,
  AnimationMixer,
  BoxGeometry,
  DoubleSide,
  LoopOnce,
  Matrix4,
  Mesh,
  MeshNormalMaterial,
  MeshStandardMaterial,
  NumberKeyframeTrack,
  Object3D,
  PerspectiveCamera,
  PlaneGeometry,
  PointLight,
  Quaternion,
  Scene,
  SpotLight,
  Vector3,
  WebGLRenderer,
  Box3
} from 'three';
import PhysicsHandler from '../../../../shared/physics/cannon/PhysicsHandler';
import { GestureType, HandTrackingResult } from '../../../../shared/scene/SceneManagerInterface';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { VRM, VRMExpressionPresetName, VRMLoaderPlugin, VRMUtils } from '@pixiv/three-vrm';
import { BVH, BVHLoader } from 'three/examples/jsm/loaders/BVHLoader';
import SkeletonHelper from '../../../../shared/model/SkeletonHelper';
import AudioHandler, { AudioDemo } from '../../../../shared/audio/AudioHandler';
import VrmSkeletonUtils from '../worker/VrmSkeletonUtils';
import SceneManagerParent from '../../../../shared/scene/SceneManagerParent';
import DanceCouple from './DanceCouple';
import Stats from "three/examples/jsm/libs/stats.module";
import HandPoseManager from '../../../../shared/hands/HandPoseManager';
import { TextMesh } from '../../../../shared/scene/text/TextMesh';

let bvhLoader = new BVHLoader();

export default class SceneManager extends SceneManagerParent  {
  private mixerDance1: AnimationMixer;
  private mixerDance2: AnimationMixer;
  private action1: AnimationAction;
  private action2: AnimationAction;
  private isAnimationPaused: boolean;
  private source1SkeletonHelper: SkeletonHelper;
  private source2SkeletonHelper: SkeletonHelper;
  private stats = new Stats();
  private readonly audioLocation = new Vector3(0, 0, 0);
  private latestGesture = GestureType.None;
  private handText: TextMesh;

    private modelOptions = {
    hip: "hip",
    preservePosition: false,
    preserveHipPosition: false,
    useTargetMatrix: true,
    rotateModel: true,
    adjustScaling: true,
    proceduralFingers: true,
    names: {
      "Normalized_J_Bip_C_Hips": "hip",
      "Normalized_J_Bip_C_Chest": "abdomen",
      "Normalized_J_Bip_C_UpperChest": "chest",
      "Normalized_J_Bip_C_Neck": "neck",
      "Normalized_J_Bip_C_Head": "head",

      "Normalized_J_Bip_R_Shoulder": "rCollar",
      "Normalized_J_Bip_R_UpperArm": "rShldr",
      "Normalized_J_Bip_R_LowerArm": "rForeArm",
      "Normalized_J_Bip_R_Hand": "rHand",

      "Normalized_J_Bip_L_Shoulder": "lCollar",
      "Normalized_J_Bip_L_UpperArm": "lShldr",
      "Normalized_J_Bip_L_LowerArm": "lForeArm",
      "Normalized_J_Bip_L_Hand": "lHand",

      "Normalized_J_Bip_L_ThumbMetacarpal": "lThumb1",
      "Normalized_J_Bip_L_ThumbProximal": "lThumb2",
      "Normalized_J_Bip_L_ThumbDistal": "lThumb3",
      "Normalized_J_Bip_L_IndexProximal": "lIndex1",
      "Normalized_J_Bip_L_IndexMiddle": "lIndex2",
      "Normalized_J_Bip_L_IndexDistal": "lIndex3",
      "Normalized_J_Bip_L_MiddleProximal": "lMid1",
      "Normalized_J_Bip_L_MiddleMiddle": "lMid2",
      "Normalized_J_Bip_L_MiddleDistal": "lMid3",
      "Normalized_J_Bip_L_RingProximal": "lRing1",
      "Normalized_J_Bip_L_RingMiddle": "lRing2",
      "Normalized_J_Bip_L_RingDistal": "lRing3",
      "Normalized_J_Bip_L_LittleProximal": "lPinky1",
      "Normalized_J_Bip_L_LittleMiddle": "lPinky2",
      "Normalized_J_Bip_L_LittleDistal": "lPinky3",

      "Normalized_J_Bip_R_ThumbMetacarpal": "rThumb1",
      "Normalized_J_Bip_R_ThumbProximal": "rThumb2",
      "Normalized_J_Bip_R_ThumbDistal": "rThumb3",
      "Normalized_J_Bip_R_IndexProximal": "rIndex1",
      "Normalized_J_Bip_R_IndexMiddle": "rIndex2",
      "Normalized_J_Bip_R_IndexDistal": "rIndex3",
      "Normalized_J_Bip_R_MiddleProximal": "rMid1",
      "Normalized_J_Bip_R_MiddleMiddle": "rMid2",
      "Normalized_J_Bip_R_MiddleDistal": "rMid3",
      "Normalized_J_Bip_R_RingProximal": "rRing1",
      "Normalized_J_Bip_R_RingMiddle": "rRing2",
      "Normalized_J_Bip_R_RingDistal": "rRing3",
      "Normalized_J_Bip_R_LittleProximal": "rPinky1",
      "Normalized_J_Bip_R_LittleMiddle": "rPinky2",
      "Normalized_J_Bip_R_LittleDistal": "rPinky3",

      "Normalized_J_Bip_R_UpperLeg": "rThigh",
      "Normalized_J_Bip_R_LowerLeg": "rShin",
      "Normalized_J_Bip_R_Foot": "rFoot",

      "Normalized_J_Bip_L_UpperLeg": "lThigh",
      "Normalized_J_Bip_L_LowerLeg": "lShin",
      "Normalized_J_Bip_L_Foot": "lFoot"
    }
  };
  private audioHandler = new AudioHandler();
  private audioElement: HTMLAudioElement;
  private bvh1: BVH;
  private bvh2: BVH;
  private bvh1Cache: BVH[] = [];
  private bvh2Cache: BVH[] = [];
  private slowDownFactor = 4;
  private danceCouples: {leader: VRM, follower: VRM}[] = [];
  private modelNames: DanceCouple[] = [
    { leader: "eric", follower: "Shina2", offsetX: -2, offsetZ: 0 },
    { leader: "eric", follower: "Shina2", offsetX: 0, offsetZ: -2 },
    { leader: "eric", follower: "Shina2", offsetX: 0, offsetZ: 2 },
    { leader: "eric", follower: "Shina2", offsetX: 2, offsetZ: 0 },
  ];
  // Optimization: every couple performs exactly the same choreography, so the
  // (expensive) retarget from the shared BVH source skeleton is computed once on a
  // reference dancer and its normalized-bone pose is copied to the other couples.
  private shareCoupleAnimation = true;
  private readonly allLeadersIdentical = this.modelNames.every(m => m.leader === this.modelNames[0].leader);
  private readonly allFollowersIdentical = this.modelNames.every(m => m.follower === this.modelNames[0].follower);
  private animationActionsEndOfDance: VRM[] = [];
  private animationMixersEndOfDance: AnimationMixer[] = [];
  private animationActionsBlink: VRM[] = [];
  private animationMixersBlink: AnimationMixer[] = [];

  private cube: Mesh;
  private cubeSound: Mesh;
  private cubeSound2: Mesh;
  private move = 1;
  private handTextFadeOut: boolean;
  private handTextOpacity = 1;

  build(camera: PerspectiveCamera, scene: Scene, renderer: WebGLRenderer, physicsHandler: PhysicsHandler) {
    super.build(camera, scene, renderer, physicsHandler);
    document.body.appendChild(this.stats.dom);

    // Optimize shadow map type for better performance and to avoid resource exhaustion
    renderer.shadowMap.type = 1; // BasicShadowMap

    const material = new MeshNormalMaterial();
    const geometry = new BoxGeometry(0.1, 0.1, 0.1);
    this.cube = new Mesh(geometry, material);

    this.cubeSound = new Mesh(geometry, material);

    this.cubeSound.position.set(0, 0, 0);

    const geometry2 = new BoxGeometry(0.3, 0.3, 0.3);
    this.cubeSound2 = new Mesh(geometry2, material);

    this.sceneHelper.addLight(true);
    const groundGeometry = new PlaneGeometry(100, 100);
    const groundMaterial = new MeshStandardMaterial({ color: 0x404040, side: DoubleSide });
    const ground = new Mesh(groundGeometry, groundMaterial);
    ground.name = "danceFloor";
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.5;
    ground.receiveShadow = true;
    this.scene.add(ground);

    // Reduced number of lights to avoid shader uniform limits
    // const pointLight1 = SceneManager.createPointLight( 0xFF7F00 );
    // const pointLight2 = SceneManager.createPointLight( 0x00FF7F );
    // const pointLight3 = SceneManager.createPointLight( 0x7F00FF );
    // const pointLight4 = SceneManager.createPointLight( 0xFFFFFF );

    // Reduced number of spotlights to avoid shader uniform limits
    // const spotLight2 = SceneManager.createSpotLight( 0x0000FF, isXR );
    // spotLight2.castShadow = false;

    renderer.shadowMap.enabled = true;
    renderer.xr.enabled = false;
    // pointLight1.position.set( 1, 1, 1 );
    // pointLight2.position.set( 2, 1, 0 );
    // pointLight3.position.set( -1, 1, -1 );
    // pointLight4.position.set( 0, 2.3, 0 );

    // spotLight2.position.set( 0, 2.3, -2 );
    // spotLight2.target.position.set( 0, -0.5, -2 );

    // scene.add( pointLight4 );
    // scene.add( pointLight1, pointLight2, pointLight3, pointLight4 );
    // scene.add( spotLight2 );
    // scene.add( spotLight2.target );

    this.audioHandler.initAudio(AudioDemo.salsaDanceSlow);
    this.audioHandler.setPosition(this.audioLocation);
    this.audioElement = this.audioHandler.audioElement;
    this.audioElement.loop = true;
    this.loadModels();
    this.handPoseManager = new HandPoseManager(scene, physicsHandler);

    const anisotropy: number = this.renderer.capabilities.getMaxAnisotropy();
    this.handText= new TextMesh(anisotropy, 1024, 512, 2, 12);
    this.handText.mesh.visible = false;
    this.scene.add(this.handText.mesh);

    // Restore original 4 spotlight setup
    const spotLight1 = SceneManager.createSpotLight( 0xFF0000, false );
    const spotLight2 = SceneManager.createSpotLight( 0x00FF00, false );
    const spotLight3 = SceneManager.createSpotLight( 0x0000FF, false );
    const spotLight4 = SceneManager.createSpotLight( 0xFFFF00, false );

    spotLight1.position.set( -2, 2.3, -2 );
    spotLight2.position.set( 2, 2.3, -2 );
    spotLight3.position.set( -2, 2.3, 2 );
    spotLight4.position.set( 2, 2.3, 2 );

    spotLight1.target.position.set( -1, -0.5, -1 );
    spotLight2.target.position.set( 1, -0.5, -1 );
    spotLight3.target.position.set( -1, -0.5, 1 );
    spotLight4.target.position.set( 1, -0.5, 1 );

    // Enable shadows for the original spotlights as requested
    spotLight1.castShadow = true;
    spotLight2.castShadow = true;
    spotLight3.castShadow = true;
    spotLight4.castShadow = true;

    this.scene.add( spotLight1, spotLight2, spotLight3, spotLight4 );
    this.scene.add( spotLight1.target, spotLight2.target, spotLight3.target, spotLight4.target );
  }

  private static createPointLight(color ) {
    const newObj = new PointLight( color, 1.5 );
    newObj.castShadow = false;
    newObj.decay = 2;
    newObj.distance = 10;
    return newObj;
  }

  private static createSpotLight(color, isXR) {
    const newObj = new SpotLight(color, 20);
    newObj.castShadow = true;
    newObj.angle = Math.PI / 6;
    newObj.penumbra = 0.2;
    newObj.decay = 2;
    newObj.distance = 20;
    if (!isXR) {
      newObj.shadow.mapSize.width = 1024;
      newObj.shadow.mapSize.height = 1024;
      newObj.shadow.camera.near = 1;
      newObj.shadow.camera.far = 20;
      newObj.shadow.bias = -0.001; // Added shadow bias
    } else {
      newObj.shadow.mapSize.width = 512;
      newObj.shadow.mapSize.height = 512;
      newObj.shadow.camera.near = 1;
      newObj.shadow.camera.far = 20;
      newObj.shadow.bias = -0.001;
    }
    return newObj;
  }

  private loadModels2(modelNames: DanceCouple[]) {
    let gltfLoader = new GLTFLoader();
    gltfLoader.register((parser) => new VRMLoaderPlugin(parser));
    for (let i = 0; i < modelNames.length; i ++) {
      console.log('Loading model ' + modelNames[i].leader);
      gltfLoader.loadAsync('../../../shared/vrm/' + modelNames[i].leader + '.vrm').then((gltf) => {
        VRMUtils.removeUnnecessaryVertices(gltf.scene);
        VRMUtils.combineSkeletons(gltf.scene);
        gltf.scene.userData.vrm = gltf.userData.vrm;
        this.scene.add(gltf.userData.vrm.scene);
        VrmSkeletonUtils.getRootBone(gltf.userData.vrm).position.x = modelNames[i].offsetX;
        VrmSkeletonUtils.getRootBone(gltf.userData.vrm).position.z = modelNames[i].offsetZ;
        gltf.scene.traverse( function( object ) {
          object.frustumCulled = false;
          if ((object as any).isMesh) {
            object.castShadow = true;
            object.receiveShadow = true;
          }
        } );
        this.initHappyAnimation(gltf.userData.vrm);
        this.initBlinkAnimation(gltf.userData.vrm);
        const leaderVrm = gltf.userData.vrm;
        leaderVrm.humanoid.getNormalizedBoneNode('leftHand').userData.vrm = leaderVrm;
        leaderVrm.humanoid.getNormalizedBoneNode('rightHand').userData.vrm = leaderVrm;
        console.log('Loading model ' + modelNames[i].follower);
        gltfLoader.loadAsync('../../../shared/vrm/' + modelNames[i].follower + '.vrm').then((gltf2) => {
          VRMUtils.removeUnnecessaryVertices(gltf2.scene);
          VRMUtils.combineSkeletons(gltf2.scene);
          gltf2.scene.userData.vrm = gltf2.userData.vrm;
          VrmSkeletonUtils.getRootBone(gltf2.userData.vrm).position.x = modelNames[i].offsetX;
          VrmSkeletonUtils.getRootBone(gltf2.userData.vrm).position.z = modelNames[i].offsetZ;
          gltf2.scene.traverse( function( object ) {
            object.frustumCulled = false;
            if ((object as any).isMesh) {
              object.castShadow = true;
              object.receiveShadow = true;
            }
          } );
          this.initHappyAnimation(gltf2.userData.vrm);
          this.initBlinkAnimation(gltf2.userData.vrm);
          const followerVrm = gltf2.userData.vrm;
          followerVrm.humanoid.getNormalizedBoneNode('leftHand').userData.vrm = followerVrm;
          followerVrm.humanoid.getNormalizedBoneNode('rightHand').userData.vrm = followerVrm;
          this.danceCouples.push({ leader: gltf.userData.vrm, follower: gltf2.userData.vrm });
          this.scene.add(gltf2.userData.vrm.scene);
          if (i == modelNames.length - 1) {
            this.loadBVH();
            this.playBlinkAnimations();
          }
        });
      });
    }
  }

  private loadModels() {
    const loader = new GLTFLoader();
    loader.load('../../../shared/models/kleeblatt.gltf', (gltf) => {
      let model = gltf.scene;
      model.scale.set(0.03, 0.03, 0.03);
      model.position.y = -0.4;
      model.traverse(function (object) {
        if ((object as any).isMesh) {
          object.castShadow = true;
          object.receiveShadow = true;
        }
      });
      this.scene.add(model);
    });
    this.loadModels2(this.modelNames);
  }

  private playBlinkAnimations() {
    for (let i = 0; i < this.animationMixersBlink.length; i++) {
      const blinkTrack = new NumberKeyframeTrack(
        this.animationActionsBlink[i].expressionManager.getExpressionTrackName( VRMExpressionPresetName.Blink ),
        [ 0.0, 0.5, 1.0 ], // times
        [ 0.0, 1.0, 0.0 ] // values
      );
      const clip = new AnimationClip( 'blink', 1, [ blinkTrack ] );
      let animationAction = this.animationMixersBlink[i].clipAction( clip ).setLoop(LoopOnce, 1);
      animationAction.play();
    }
    setTimeout( () => {
        this.playBlinkAnimations();
      }, 4000
    )
  }

  private initBlinkAnimation(dancer: VRM) {
    let mixerBlink = new AnimationMixer( dancer.scene );
    this.animationActionsBlink.push(dancer);
    this.animationMixersBlink.push(mixerBlink);
  }

  private initHappyAnimation(dancer: VRM) {
    let mixerHappy = new AnimationMixer( dancer.scene );
    this.animationActionsEndOfDance.push(dancer);
    this.animationMixersEndOfDance.push(mixerHappy);
  }

  initialLoadBVH() {
    let moveStr = "" + this.move;
    if (this.move < 10) {
       moveStr = "0" + this.move;
    }
    bvhLoader.load("../../../shared/bvh/60/60_" + moveStr + "_scaled.bvh", (bvh) => {
      this.bvh1 = bvh;
      this.bvh1Cache[this.move] = bvh;
      this.source1SkeletonHelper = new SkeletonHelper(bvh.skeleton.bones[0]);
      this.source1SkeletonHelper.skeleton = bvh.skeleton;
      bvhLoader.load("../../../shared/bvh/61/61_" + moveStr + "_scaled.bvh", (bvh) => {
        this.bvh2 = bvh;
        this.bvh2Cache[this.move] = bvh;
        this.source2SkeletonHelper = new SkeletonHelper(bvh.skeleton.bones[0]);
        this.source2SkeletonHelper.skeleton = bvh.skeleton;
        this.startShow();
      });
    });
  }

  doNext() {
    if (this.bvh1Cache[this.move]) {
      this.bvh1 = this.bvh1Cache[this.move];
      this.bvh2 = this.bvh2Cache[this.move];
      this.startMove1();
    } else {
      this.loadBVH();
    }
  }

  private startMove1() {
    this.source1SkeletonHelper = new SkeletonHelper(this.bvh1.skeleton.bones[0]);
    this.source1SkeletonHelper.skeleton = this.bvh1.skeleton;
    this.source2SkeletonHelper = new SkeletonHelper(this.bvh2.skeleton.bones[0]);
    this.source2SkeletonHelper.skeleton = this.bvh2.skeleton;
    this.startShow();
  }

  loadBVH() {
    let moveStr = "" + this.move;
    if (this.move < 10) {
      moveStr = "0" + this.move;
    }
    bvhLoader.load("../../../shared/bvh/60/60_" + moveStr + "_scaled.bvh", (bvh) => {
      this.bvh1Cache[this.move] = bvh;
      this.bvh1 = bvh;
      bvhLoader.load("../../../shared/bvh/61/61_" + moveStr + "_scaled.bvh", (bvh) => {
        this.bvh2Cache[this.move] = bvh;
        this.bvh2 = bvh;
        this.startMove1();
       });
    });
  }

  private startShow() {
    console.log("Play move " + this.move + " for second: " + this.bvh1.clip.duration * this.slowDownFactor);
    this.isAnimationPaused = false;
    this.mixerDance1 = new AnimationMixer(this.source1SkeletonHelper);
    this.mixerDance2 = new AnimationMixer(this.source2SkeletonHelper);
    console.log("Start animation");
    this.action1 = this.mixerDance1.clipAction(this.bvh1.clip);
    this.action1.clampWhenFinished = true;
    this.action1.play();
    this.action2 = this.mixerDance2.clipAction(this.bvh2.clip);
    this.action2.clampWhenFinished = true;
    this.action2.play();
    this.slowDownFactor = 1.83
    this.audioElement.play();
  }

  private pauseShow() {
    this.isAnimationPaused = true;
    console.log("Pause animation");
  }

  private resumeShow() {
    this.isAnimationPaused = false;
    console.log("Resume animation");
  }

  private playReverse() {
    this.action1.timeScale = -this.action1.timeScale;
    this.action2.timeScale = -this.action2.timeScale;
  }

  private playSpeed(delta) {
    this.slowDownFactor += delta;
    if (this.slowDownFactor > 5) {
      this.slowDownFactor = 2;
    }
  }

  update() {
    super.update();
    let delta = this.timer.getDelta();
    if (this.handTextFadeOut) {
      this.handTextOpacity -= 0.02;
      if (this.handTextOpacity < 0) {
        this.handTextOpacity = 0;
        this.handTextFadeOut = false;
        this.handText.mesh.visible = false;
      }
      this.handText.fadeOut(this.handTextOpacity);
    }
    if (this.mixerDance1 && this.mixerDance2) {
      if (!this.isAnimationPaused) {
        this.mixerDance1.update(delta/this.slowDownFactor);
        this.mixerDance2.update(delta/this.slowDownFactor);
        if (this.danceCouples && this.danceCouples.length > 0) {
          if (this.shareCoupleAnimation && this.allLeadersIdentical && this.allFollowersIdentical) {
            // Compute the retargeted pose ONCE on the reference couple.
            const refLeader = this.danceCouples[0].leader;
            const refFollower = this.danceCouples[0].follower;
            VrmSkeletonUtils.retarget(refLeader, this.source1SkeletonHelper, this.modelOptions);
            VrmSkeletonUtils.retarget(refFollower, this.source2SkeletonHelper, this.modelOptions);

            // Reuse that pose for every other couple by copying the normalized-bone
            // transforms; this is bit-identical to running retarget per couple.
            for (let i = 1; i < this.danceCouples.length; i++) {
              this.copyNormalizedPose(refLeader, this.danceCouples[i].leader);
              this.copyNormalizedPose(refFollower, this.danceCouples[i].follower);
            }

            // Propagate to each instance's skinned skeleton + spring bones.
            for (let i = 0; i < this.danceCouples.length; i++) {
              this.danceCouples[i].leader.update(delta);
              this.danceCouples[i].follower.update(delta);
            }

            // Feet-on-ground correction is identical for identical poses:
            // measure once per archetype and apply the same offset to every couple.
            const leaderOffset = this.computeGroundOffset(refLeader);
            const followerOffset = this.computeGroundOffset(refFollower);
            for (let i = 0; i < this.danceCouples.length; i++) {
              this.danceCouples[i].leader.scene.position.y -= leaderOffset;
              this.danceCouples[i].follower.scene.position.y -= followerOffset;
            }
          } else {
            // Fallback: retarget every couple independently (couples differ).
            for (let i = 0; i < this.danceCouples.length; i++) {
              VrmSkeletonUtils.retarget(this.danceCouples[i].leader, this.source1SkeletonHelper, this.modelOptions);
              VrmSkeletonUtils.retarget(this.danceCouples[i].follower, this.source2SkeletonHelper, this.modelOptions);
              this.danceCouples[i].leader.update(delta);
              this.danceCouples[i].follower.update(delta);

              // Adjust Y position to keep feet on the ground
              [this.danceCouples[i].leader, this.danceCouples[i].follower].forEach(vrm => {
                vrm.scene.position.y -= this.computeGroundOffset(vrm);
              });
            }
          }
        }
        if (this.animationMixersEndOfDance.length > 0) {
          for (let i = 0; i < this.animationMixersEndOfDance.length; i++) {
            this.animationMixersEndOfDance[i].update(delta);
          }
        }
      }
    }
    if (this.animationMixersBlink.length > 0) {
      for (let i = 0; i < this.animationMixersBlink.length; i++) {
        this.animationMixersBlink[i].update(delta);
      }
    }

    const position = new Vector3(this.camera.position.x, this.camera.position.y, this.camera.position.z);
    const quat: Quaternion = new Quaternion(-this.camera.quaternion.x, -this.camera.quaternion.y, -this.camera.quaternion.z, -this.camera.quaternion.w);
    const direction3 = this.audioLocation.applyQuaternion(quat);
    const direction4 = position.sub(direction3);

    this.cubeSound2.position.set(position.x - direction4.x, position.y - direction4.y, position.z - direction4.z);
    this.audioHandler.setPosition(direction4);
    this.audioHandler.setVolume(direction4);
  }

  // Copies the normalized-bone pose (position/rotation/scale) from a reference VRM
  // onto another instance of the same model. Walks the humanoid bone tree from the
  // hips down; both VRMs share an identical hierarchy so we can match children by index.
  private copyNormalizedPose(src: VRM, dst: VRM) {
    const srcHips = src.humanoid.getNormalizedBoneNode('hips');
    const dstHips = dst.humanoid.getNormalizedBoneNode('hips');
    if (srcHips && dstHips) {
      this.copyBoneTransforms(srcHips, dstHips);
    }
  }

  private copyBoneTransforms(src: Object3D, dst: Object3D) {
    dst.position.copy(src.position);
    dst.quaternion.copy(src.quaternion);
    dst.scale.copy(src.scale);
    const count = Math.min(src.children.length, dst.children.length);
    for (let i = 0; i < count; i++) {
      this.copyBoneTransforms(src.children[i], dst.children[i]);
    }
  }

  // Returns how far the dancer must be lowered so its lowest mesh point sits on the
  // floor (y = -0.5). Identical poses of the same model yield the same offset.
  private computeGroundOffset(vrm: VRM): number {
    vrm.scene.updateMatrixWorld(true);
    const box = new Box3();
    vrm.scene.traverse(obj => {
      if ((obj as any).isMesh) {
        box.expandByObject(obj);
      }
    });
    return box.min.y + 0.5;
  }

  updateHandPose(result) {
    if (this.handPoseManager) {
      this.handPoseManager.renderHands(result);
      if (this.isAnimationPaused) {
        if (this.handPoseManager.isOpenHand()) {
          console.log('Hand open');
          this.resumeShow();
        }
      } else {
        if (this.handPoseManager.isStopHand()) {
          this.pauseShow();
          console.log('Hand stop!');
        }
      }
    }
  }

  displayMessage(message: string, o: Vector3, q: Quaternion) {
    if (o) {
      this.handText.mesh.visible = true;
      this.handText.mesh.position.set(o.x, o.y, o.z);
      let rotationMatrix = new Matrix4();
      if (this.camera.position) {
        rotationMatrix.lookAt(this.camera.position, o, this.handText.mesh.up);
        this.handText.mesh.quaternion.setFromRotationMatrix(rotationMatrix);
        this.handText.set(message);
        this.handTextOpacity = 1;
        this.handTextFadeOut = true;
      }
    }
  }

  handleGesture(gesture: HandTrackingResult) {
    if (gesture.gestureType != GestureType.None && gesture.gestureType != this.latestGesture) {
      this.latestGesture = gesture.gestureType;
      if (gesture.gestureType == GestureType.Open_Hand) {
        if (this.isAnimationPaused) {
          this.displayMessage("Resume", gesture.position, gesture.orientation)
          this.resumeShow();
        }
      } else if (gesture.gestureType == GestureType.Middle_Thumb) {
        if (this.isAnimationPaused) {
        } else {
          this.displayMessage("Pause", gesture.position, gesture.orientation)
          this.pauseShow();
        }
      } else if (gesture.gestureType == GestureType.Closed_Hand) {
        if (this.isAnimationPaused) {
          this.displayMessage("Reverse animation", gesture.position, gesture.orientation)
          this.playReverse();
        }
      } else if (gesture.gestureType == GestureType.Index_Thumb) {
        if (this.isAnimationPaused) {
          this.displayMessage("Animation slow down factor: " + this.slowDownFactor, gesture.position, gesture.orientation)
          this.playSpeed(1);
        }
      } else if (gesture.gestureType == GestureType.Ring_Thumb) {
        if (this.isAnimationPaused) {
          this.move = this.move + 1;
          console.log("Play move " + this.move);
          if (this.move > 15) this.move = 1;
          this.displayMessage("Next move: " + this.move, gesture.position, gesture.orientation)
          this.doNext();
        }
      } else if (gesture.gestureType == GestureType.Middle_Thumb_Upward) {
        this.renderer.shadowMap.enabled = !this.renderer.shadowMap.enabled;
        this.displayMessage("Shadows: " + (this.renderer.shadowMap.enabled ? "ON" : "OFF"), gesture.position, gesture.orientation);
      } else if (gesture.gestureType == GestureType.Pinky_Thumb) {
        if (this.isAnimationPaused) {
          this.move = this.move - 1;
          console.log("Play move " + this.move);
          if (this.move < 1) this.move = 15;
          this.displayMessage("Previous move: " + this.move, gesture.position, gesture.orientation)
          this.doNext();
        }
      }
    }
  }

  getInitialCameraPosition(): Vector3 {
    return new Vector3(0, 0.75, 0);
  }

}
